import type { ServicesPricingOfferingVisualTone } from "./profileServicesPricingDisplay";

export type ServicesPricingOfferingIconKey =
  | "box"
  | "thermometer"
  | "video"
  | "camera"
  | "map"
  | "tractor"
  | "building"
  | "eye";

export type ServicesPricingOfferingIconPresentation = Readonly<{
  key: ServicesPricingOfferingIconKey;
  label: string;
  tone: ServicesPricingOfferingVisualTone;
  color: string;
  bg: string;
}>;

export const SERVICES_PRICING_OFFERING_ICON_OPTIONS: readonly ServicesPricingOfferingIconPresentation[] = [
  { key: "box", label: "3D / LiDAR", tone: "modeling", color: "#F97316", bg: "#FFEDD5" },
  { key: "thermometer", label: "Thermal", tone: "thermal", color: "#EF4444", bg: "#FEE2E2" },
  { key: "video", label: "Video", tone: "marketing", color: "#A855F7", bg: "#F3E8FF" },
  { key: "camera", label: "Photo", tone: "marketing", color: "#EC4899", bg: "#FCE7F3" },
  { key: "map", label: "Mapping", tone: "documentation", color: "#2563EB", bg: "#DBEAFE" },
  { key: "tractor", label: "Agriculture", tone: "agriculture", color: "#10B981", bg: "#DCFCE7" },
  { key: "building", label: "Construction", tone: "documentation", color: "#6366F1", bg: "#E0E7FF" },
  { key: "eye", label: "Inspection", tone: "default", color: "#7C3AED", bg: "#EDE9FE" },
];

const OFFERING_ICON_KEY_SET = new Set<string>(
  SERVICES_PRICING_OFFERING_ICON_OPTIONS.map((option) => option.key),
);

export function isServicesPricingOfferingIconKey(
  value: string,
): value is ServicesPricingOfferingIconKey {
  return OFFERING_ICON_KEY_SET.has(value);
}

export function getServicesPricingOfferingIconPresentation(
  iconKey: ServicesPricingOfferingIconKey,
): ServicesPricingOfferingIconPresentation {
  return SERVICES_PRICING_OFFERING_ICON_OPTIONS.find((option) => option.key === iconKey)
    ?? SERVICES_PRICING_OFFERING_ICON_OPTIONS.find((option) => option.key === "map")!;
}

export function inferServicesPricingOfferingIconKey(input: Readonly<{
  serviceCategory: string;
  serviceTitle: string;
  serviceTagLabels?: readonly string[];
}>): ServicesPricingOfferingIconKey {
  const haystack = `${input.serviceCategory} ${input.serviceTitle} ${(input.serviceTagLabels ?? []).join(" ")}`.toLowerCase();

  if (haystack.includes("thermal")) return "thermometer";
  if (haystack.includes("agriculture") || haystack.includes("ndvi") || haystack.includes("crop")) return "tractor";
  if (haystack.includes("lidar") || haystack.includes("point cloud")) return "box";
  if (haystack.includes("photogrammetry") || haystack.includes("3d model")) return "box";
  if (haystack.includes("video") || haystack.includes("photo") || haystack.includes("cinema") || haystack.includes("real estate")) return "video";
  if (haystack.includes("construction") || haystack.includes("documentation") || haystack.includes("progress")) return "building";
  if (haystack.includes("inspect")) return "eye";
  if (haystack.includes("mapping") || haystack.includes("survey")) return "map";
  return "map";
}

export function resolveServicesPricingOfferingIconKey(input: Readonly<{
  iconKey?: string;
  serviceCategory: string;
  serviceTitle: string;
  serviceTagLabels?: readonly string[];
}>): ServicesPricingOfferingIconKey {
  if (input.iconKey && isServicesPricingOfferingIconKey(input.iconKey)) {
    return input.iconKey;
  }
  return inferServicesPricingOfferingIconKey(input);
}

export function inferServicesPricingSkillIconKey(skillKey: string): ServicesPricingOfferingIconKey {
  const key = skillKey.toLowerCase();
  if (key.includes("thermal")) return "thermometer";
  if (key.includes("video") || key.includes("photo") || key.includes("cinemat")) return "camera";
  if (key.includes("inspect")) return "eye";
  if (key.includes("lidar") || key.includes("3d") || key.includes("model") || key.includes("photogrammetry")) return "box";
  if (key.includes("map") || key.includes("survey")) return "map";
  return "map";
}

export function resolveServicesPricingSkillIconKey(
  skillKey: string,
  iconKey?: string,
): ServicesPricingOfferingIconKey {
  if (iconKey && isServicesPricingOfferingIconKey(iconKey)) {
    return iconKey;
  }
  return inferServicesPricingSkillIconKey(skillKey);
}
